// Phase 90.C.1 — Referee ↔ Admin voice call context (LiveKit Cloud).
// v17.10 — WS Multiplex: subscribe channel `notifications` qua wsHub singleton
// (thay socket riêng cũ). Server broadcast `call.*` payloads qua
// `hub.push(user_id, payload)` → fanout tới hub_bus notifications.
//
// Provides a single global place that:
//   • subscribe `notifications` channel qua wsHub để nhận call.incoming/
//     answered/ended (payload không wrap thêm — chỉ raw type + fields).
//   • tracks the current call state (idle / outgoing / incoming / active).
//   • connects to a LiveKit Cloud room for audio (voice-only MVP).
//   • exposes `initiateCall(peer, tournament)` and mounts a floating modal.
//
// Consumers: import { useCall } from "../contexts/CallContext".

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { subscribe as wsSubscribe } from "../lib/wsHub";
import { useAuth } from "./AuthContext";

const CallCtx = createContext(null);

// v17 Lazy Load Đợt 1 — `livekit-client` là ~450KB raw (~140KB gzip). Đưa ra
// khỏi initial bundle: chỉ dynamic-import khi thật sự cần (bấm gọi / có
// cuộc gọi đến). Promise được cache module-scope nên các call sau nhận
// module ngay lập tức, không có race giữa các consumer.
let _lkModulePromise = null;
function loadLiveKit() {
  if (!_lkModulePromise) _lkModulePromise = import("livekit-client");
  return _lkModulePromise;
}

const IDLE_STATE = { phase: "idle", call: null, peer: null };

// Phase 90.E — App mobile PickLeague là WebView shell: trên Android, WebView
// không tự xin OS permission RECORD_AUDIO khi web gọi getUserMedia. Báo native
// shell xin quyền SỚM (lúc bắt đầu gọi / đổ chuông) qua postMessage bridge.
function requestNativeMicPermission() {
  try {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_MIC_PERMISSION" }));
  } catch { /* không chạy trong app shell */ }
}

// v16.73 — Điều hướng audio route trong app shell (iOS/Android): mặc định
// LOA THOẠI TRONG (earpiece) cho đỡ ồn, user toggle sang loa ngoài nếu muốn.
// mode: "earpiece" | "speaker" | "stop" — native WebViewScreen xử lý qua
// @livekit/react-native AudioSession (JS-only → OTA được, không cần rebuild).
function postNativeAudioRoute(mode) {
  if (typeof window === "undefined" || window.__pickleagueNativeApp !== true) return;
  try {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "CALL_AUDIO_ROUTE", mode }));
  } catch { /* noop */ }
}

// Container riêng để giữ các <audio> element của remote tracks. Đặt ngoài
// React tree để không bị unmount khi state đổi.
const AUDIO_HOST_ID = "__pl_livekit_audio_host__";
function ensureAudioHost() {
  if (typeof document === "undefined") return null;
  let el = document.getElementById(AUDIO_HOST_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = AUDIO_HOST_ID;
    el.style.position = "fixed";
    el.style.left = "-9999px";
    el.style.top = "-9999px";
    document.body.appendChild(el);
  }
  return el;
}
function clearAudioHost() {
  const host = document.getElementById(AUDIO_HOST_ID);
  if (host) host.innerHTML = "";
}

export function CallProvider({ children }) {
  const { user } = useAuth() || {};
  const [state, setState] = useState(IDLE_STATE); // phase: idle|preparing|outgoing|incoming|active|ending
  const [muted, setMuted] = useState(false);
  const [remoteConnected, setRemoteConnected] = useState(false);
  // Mobile browsers (iOS Safari / Android Chrome) chặn autoplay audio remote
  // nếu không có user gesture → bên nhận không nghe gì dù track đã subscribe.
  const [audioBlocked, setAudioBlocked] = useState(false);
  // v16.73 — Trạng thái loa ngoài (chỉ có ý nghĩa trong app shell). Default
  // false = loa thoại trong (earpiece).
  const [speakerOn, setSpeakerOn] = useState(false);
  const roomRef = useRef(null);
  const roomNameRef = useRef(null);
  const roomConnectPromiseRef = useRef(null);
  const micPublishedRef = useRef(false);
  const answeringRef = useRef(false);
  const joiningRef = useRef(false);
  const callAttemptRef = useRef(0);
  const nativeIncomingSentRef = useRef(new Map());
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const postNativeIncomingCall = useCallback((call) => {
    const callId = call?.call_id || call?.id;
    if (!callId || typeof window === "undefined" || window.__pickleagueNativeApp !== true) return false;
    const now = Date.now();
    const storageKey = `pl_native_incoming_${callId}`;
    let previousAt = nativeIncomingSentRef.current.get(callId) || 0;
    try { previousAt = Math.max(previousAt, Number(window.sessionStorage?.getItem(storageKey) || 0)); } catch { /* noop */ }
    if (now - previousAt < 90_000) {
      console.info("[PL_CALL_WEB] native_incoming_duplicate_ignored", { callId });
      return true;
    }
    nativeIncomingSentRef.current.set(callId, now);
    try { window.sessionStorage?.setItem(storageKey, String(now)); } catch { /* noop */ }
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "SHOW_NATIVE_INCOMING_CALL", call }));
    return true;
  }, []);

  // 25-07 (Bug 2 fix) — Pre-warm mic track NGAY khi đổ chuông / gọi đi:
  // getUserMedia là phần chậm nhất (~1-2s trên mobile). Xin mic TRƯỚC lúc
  // bấm nghe → khi answer chỉ cần publish track có sẵn → gần như tức thì.
  const prewarmedMicRef = useRef(null);
  const prewarmMic = useCallback(async () => {
    if (prewarmedMicRef.current) return;
    try {
      const { createLocalAudioTrack } = await loadLiveKit();
      prewarmedMicRef.current = await createLocalAudioTrack();
    } catch { /* quyền mic fail — joinRoom fallback setMicrophoneEnabled */ }
  }, []);
  const stopPrewarmedMic = useCallback(() => {
    try { prewarmedMicRef.current?.stop(); } catch { /* noop */ }
    prewarmedMicRef.current = null;
  }, []);

  const cleanupRoom = useCallback(async () => {
    const room = roomRef.current;
    roomRef.current = null;
    roomNameRef.current = null;
    roomConnectPromiseRef.current = null;
    micPublishedRef.current = false;
    if (room) {
      try { await room.disconnect(); } catch { /* noop */ }
    }
    stopPrewarmedMic();
    clearAudioHost();
    // v16.73 — Trả audio route về mặc định khi kết thúc cuộc gọi (app shell).
    postNativeAudioRoute("stop");
    setSpeakerOn(false);
    setMuted(false);
    setRemoteConnected(false);
    setAudioBlocked(false);
  }, [stopPrewarmedMic]);

  const resetIdle = useCallback(async () => {
    await cleanupRoom();
    setState(IDLE_STATE);
  }, [cleanupRoom]);

  const publishMicrophone = useCallback(async (room) => {
    if (!room || micPublishedRef.current) return;
    const pre = prewarmedMicRef.current;
    prewarmedMicRef.current = null;
    try {
      if (pre) {
        const { Track } = await loadLiveKit();
        await room.localParticipant.publishTrack(pre, { source: Track.Source.Microphone });
      } else {
        await room.localParticipant.setMicrophoneEnabled(true);
      }
      micPublishedRef.current = true;
    } catch {
      try { pre?.stop(); } catch { /* noop */ }
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
        micPublishedRef.current = true;
      } catch (e2) {
        toast.error("Không thể bật microphone. Kiểm tra quyền truy cập trong trình duyệt.");
        throw e2;
      }
    }
  }, []);

  // Kết nối signaling/ICE có thể mất vài giây trên mobile. Hai phía join room
  // ngay lúc đổ chuông nhưng CHƯA publish mic; khi bấm nhận chỉ publish track
  // đã pre-warm nên trạng thái active gần như tức thì và không rò audio sớm.
  const joinRoom = useCallback(async (
    { livekit_url, livekit_token, room_name },
    { publishMic = true } = {},
  ) => {
    if (!livekit_url || !livekit_token) throw new Error("Thiếu cấu hình LiveKit");
    if (roomRef.current && roomNameRef.current === room_name && roomConnectPromiseRef.current) {
      const existingRoom = await roomConnectPromiseRef.current;
      if (publishMic) await publishMicrophone(existingRoom);
      try { await existingRoom.startAudio(); } catch { setAudioBlocked(true); }
      return existingRoom;
    }
    if (roomRef.current) {
      try { await roomRef.current.disconnect(); } catch { /* noop */ }
    }
    const LK = await loadLiveKit();
    const { Room, RoomEvent, Track } = LK;
    const room = new Room({ adaptiveStream: false, dynacast: false });
    roomRef.current = room;
    roomNameRef.current = room_name || null;
    micPublishedRef.current = false;

    const host = ensureAudioHost();
    // v16.73 — App shell: route audio về LOA THOẠI TRONG ngay khi vào room
    // (mặc định đỡ ồn — user có thể bật loa ngoài bằng nút trong CallModal).
    postNativeAudioRoute("earpiece");
    setSpeakerOn(false);

    // Attach an audio track from a remote participant into our hidden host.
    // Guarded by Source.Microphone to ignore unrelated audio (screen audio,
    // custom tracks). Idempotent: skips if an <audio> already exists for that
    // publication SID.
    const attachMicTrack = (track, publication, participant) => {
      try {
        if (!track || track.kind !== Track.Kind.Audio) return;
        // Voice-only MVP: only accept microphone source. Our token also
        // restricts publish sources to microphone, but defense in depth.
        const src = track.source || publication?.source;
        if (src && src !== Track.Source.Microphone) return;
        const sid = publication?.trackSid || track.sid || "";
        if (sid && host?.querySelector(`[data-lk-sid="${sid}"]`)) return; // already attached
        const el = track.attach();
        el.setAttribute("data-lk-audio", participant?.identity || "");
        if (sid) el.setAttribute("data-lk-sid", sid);
        el.autoplay = true;
        el.playsInline = true;
        if (host) host.appendChild(el);
        // Mobile Safari/Chrome chặn autoplay khi play() ngoài user gesture →
        // bật cờ audioBlocked để CallModal hiện nút "Bật âm thanh".
        Promise.resolve(el.play?.()).catch(() => { setAudioBlocked(true); });
        setRemoteConnected(true);
      } catch { /* noop */ }
    };

    // Bug fix 1: LiveKit không tự phát audio remote — phải TrackSubscribed +
    // track.attach() để tạo <audio autoplay>. Không có bước này thì cả 2 bên
    // đều publish mic nhưng không ai nghe được.
    room.on(RoomEvent.TrackSubscribed, attachMicTrack);
    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      try {
        // detach() trả về HTMLMediaElement[]; remove khỏi DOM để tránh leak.
        track.detach().forEach((el) => el.remove());
      } catch { /* noop */ }
    });

    room.on(RoomEvent.ParticipantConnected, () => setRemoteConnected(true));
    room.on(RoomEvent.ParticipantDisconnected, () => setRemoteConnected(false));

    // Bug fix mobile: theo dõi trạng thái playback — nếu browser chặn thì
    // canPlaybackAudio=false → hiện nút bật âm thanh.
    room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
      setAudioBlocked(!room.canPlaybackAudio);
    });

    // Reconnect: LiveKit giữ subscription qua reconnect nhưng <audio> element
    // ở host có thể đã bị unmount (unlikely trong flow này, nhưng để chắc,
    // re-attach lại toàn bộ mic track hiện đang subscribed).
    room.on(RoomEvent.Reconnected, () => {
      room.remoteParticipants.forEach((p) => {
        p.audioTrackPublications.forEach((pub) => {
          if (pub.track && pub.isSubscribed) {
            attachMicTrack(pub.track, pub, p);
          }
        });
      });
    });

    // Bug fix 2: khi room bị disconnect (peer hangup, network drop, LiveKit
    // đá ra, …) phải reset state về idle. Trước đây chỉ set 'ending' → state
    // stuck → cuộc gọi tiếp theo tới bị auto-reject vì `phase !== 'idle'`.
    room.on(RoomEvent.Disconnected, () => {
      const cur = stateRef.current;
      const keepRinging = !micPublishedRef.current && ["outgoing", "incoming"].includes(cur.phase);
      clearAudioHost();
      setRemoteConnected(false);
      setMuted(false);
      setAudioBlocked(false);
      roomRef.current = null;
      roomNameRef.current = null;
      roomConnectPromiseRef.current = null;
      micPublishedRef.current = false;
      if (!keepRinging) setState(IDLE_STATE);
    });

    const connectPromise = room.connect(livekit_url, livekit_token).then(() => room);
    roomConnectPromiseRef.current = connectPromise;
    try {
      await connectPromise;
    } catch (error) {
      if (roomRef.current === room) {
        roomRef.current = null;
        roomNameRef.current = null;
        roomConnectPromiseRef.current = null;
      }
      try { await room.disconnect(); } catch { /* noop */ }
      throw error;
    }
    if (publishMic) await publishMicrophone(room);
    // Attach những track remote đã sẵn có (nếu peer join trước).
    room.remoteParticipants.forEach((p) => {
      p.audioTrackPublications.forEach((pub) => {
        if (pub.track) attachMicTrack(pub.track, pub, p);
      });
    });
    // Mở khoá audio playback (quan trọng trên mobile). Nếu bị chặn,
    // AudioPlaybackStatusChanged/catch sẽ bật cờ audioBlocked.
    try {
      await room.startAudio();
      setAudioBlocked(false);
    } catch {
      setAudioBlocked(true);
    }
    if (room.remoteParticipants.size > 0) setRemoteConnected(true);
    return room;
  }, [publishMicrophone]);

  // Người dùng bấm nút "Bật âm thanh" (user gesture) → unlock playback.
  const enableAudio = useCallback(async () => {
    const room = roomRef.current;
    try {
      if (room) await room.startAudio();
      const host = document.getElementById(AUDIO_HOST_ID);
      host?.querySelectorAll("audio").forEach((el) => {
        try { el.play?.().catch(() => {}); } catch { /* noop */ }
      });
      setAudioBlocked(false);
    } catch { /* vẫn bị chặn — giữ nút */ }
  }, []);

  // ─── Public API: initiate outgoing call ───
  const initiateCall = useCallback(
    async ({ target_user_id, tournament_id, peer }) => {
      if (!user?.id) {
        toast.error("Vui lòng đăng nhập");
        return;
      }
      if (state.phase !== "idle") {
        toast.info("Đang có cuộc gọi khác đang diễn ra");
        return;
      }
      const attemptId = ++callAttemptRef.current;
      let preparedCallId = null;
      try {
        requestNativeMicPermission();
        prewarmMic();
        setState({ phase: "preparing", call: null, peer: peer || null });
        const { data } = await api.post("/calls/initiate", {
          target_user_id,
          tournament_id,
          defer_ring: true,
        });
        preparedCallId = data.call_id;
        if (attemptId !== callAttemptRef.current) {
          api.post(`/calls/${data.call_id}/reject`).catch(() => {});
          return;
        }
        setState({ phase: "preparing", call: data, peer: data.peer || peer || null });
        // Hai pha: caller phải connected/muted TRƯỚC, sau đó backend mới gửi
        // WS/FCM để callee đổ chuông. Nhờ vậy callee Answer tức thì không thể
        // tới trước lúc caller có Room/token.
        await joinRoom(data, { publishMic: false });
        if (attemptId !== callAttemptRef.current) return;
        const ringingState = { phase: "outgoing", call: data, peer: data.peer || peer || null };
        stateRef.current = ringingState;
        setState(ringingState);
        console.info("[PL_CALL_WEB] caller_preconnected", { callId: data.call_id });
        if (data.status === "prepared") {
          await api.post(`/calls/${data.call_id}/ring`);
          console.info("[PL_CALL_WEB] ring_dispatched", { callId: data.call_id });
        } else {
          // Backend cũ không trả status và đã đổ chuông ngay trong /initiate.
          console.info("[PL_CALL_WEB] legacy_ring_already_dispatched", { callId: data.call_id });
        }
        // Auto-timeout ringing after 45s
        setTimeout(() => {
          const current = stateRef.current;
          if (current.phase === "outgoing" && current.call?.call_id === data.call_id) {
            api.post(`/calls/${data.call_id}/reject`).catch(() => {});
            toast.info("Không có phản hồi");
            resetIdle();
          }
        }, 45_000);
      } catch (e) {
        if (preparedCallId) api.post(`/calls/${preparedCallId}/reject`).catch(() => {});
        if (attemptId !== callAttemptRef.current) return;
        const msg = e?.response?.data?.detail || "Không thể gọi";
        toast.error(msg);
        await resetIdle();
      }
    },
    [state.phase, user?.id, prewarmMic, joinRoom, resetIdle],
  );

  // ─── Public API: answer incoming ───
  const answerCall = useCallback(async () => {
    if (state.phase !== "incoming" || !state.call?.call_id) return;
    // Chống double-click: click lần 2 sẽ POST /answer lần nữa → 409 "active"
    // → toast lỗi + thoát cuộc gọi. Guard bằng ref.
    if (answeringRef.current) return;
    const isNativeShell = typeof window !== "undefined" && window.__pickleagueNativeApp === true;
    if (isNativeShell) {
      window.ReactNativeWebView?.postMessage(JSON.stringify({
        type: "ANSWER_CALL_NATIVE",
        call: {
          ...state.call,
          callerName: state.peer?.name,
          callerAvatar: state.peer?.avatar_url,
        },
      }));
      return;
    }
    answeringRef.current = true;
    requestNativeMicPermission();
    prewarmMic();
    const pendingCall = state.call;
    const pendingPeer = state.peer;
    // Optimistic UI: chuyển ngay sang "active/Đang kết nối…" để nút bắt máy
    // biến mất tức thì, không bị cảm giác treo trong lúc join LiveKit room.
    setState({ phase: "active", call: pendingCall, peer: pendingPeer });
    try {
      const { data } = await api.post(`/calls/${pendingCall.call_id}/answer`);
      await joinRoom(data);
      setState({ phase: "active", call: data, peer: data.peer || pendingPeer });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Không thể trả lời cuộc gọi";
      toast.error(msg);
      await resetIdle();
    } finally {
      answeringRef.current = false;
    }
  }, [state, joinRoom, resetIdle, prewarmMic]);

  // ─── When outgoing call gets answered by peer, join the room ourselves ───
  const onPeerAnswered = useCallback(async () => {
    if (joiningRef.current) return;
    const currentCall = stateRef.current;
    if (currentCall.phase !== "outgoing" || !currentCall.call) return;
    joiningRef.current = true;
    console.info("[PL_CALL_WEB] peer_answered", { callId: currentCall.call.call_id });
    // Backend đã active → đổi UI ngay; preconnected room chỉ còn publish mic.
    setState({ phase: "active", call: currentCall.call, peer: currentCall.peer });
    try {
      await joinRoom(currentCall.call);
      console.info("[PL_CALL_WEB] caller_mic_published", { callId: currentCall.call.call_id });
    } catch {
      await resetIdle();
    } finally {
      joiningRef.current = false;
    }
  }, [joinRoom, resetIdle]);

  // ─── Public API: reject / hangup ───
  const rejectCall = useCallback(async () => {
    callAttemptRef.current += 1;
    if (!state.call?.call_id) return resetIdle();
    try {
      await api.post(`/calls/${state.call.call_id}/reject`);
    } catch { /* noop */ }
    await resetIdle();
  }, [state.call, resetIdle]);

  const endCall = useCallback(async () => {
    if (!state.call?.call_id) return resetIdle();
    try {
      await api.post(`/calls/${state.call.call_id}/end`);
    } catch { /* noop */ }
    await resetIdle();
  }, [state.call, resetIdle]);

  const toggleMute = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !muted;
    try {
      await room.localParticipant.setMicrophoneEnabled(!next);
      setMuted(next);
    } catch { /* noop */ }
  }, [muted]);

  // v16.73 — Toggle loa ngoài ↔ loa thoại trong (chỉ app shell).
  const toggleSpeaker = useCallback(() => {
    const next = !speakerOn;
    postNativeAudioRoute(next ? "speaker" : "earpiece");
    setSpeakerOn(next);
  }, [speakerOn]);

  // ─── WebSocket subscription for signalling ───
  // v17.10 — Migrated to wsHub singleton (channel `notifications`). Hub tự
  // xử lý ticket auth + reconnect + backoff. Legacy socket cũ đã bỏ hoàn
  // toàn — cả app chỉ còn 1 kết nối `/api/ws/hub`.
  useEffect(() => {
    if (!user?.id) return undefined;

    const onMsg = (msg) => {
      const t = msg?.type;
      if (!t || !t.startsWith("call.")) return;
      if (t === "call.incoming") {
        const isNativeShell = typeof window !== "undefined" && window.__pickleagueNativeApp === true;
        if (isNativeShell) {
          postNativeIncomingCall({
            call_id: msg.call_id,
            room_name: msg.room_name,
            tournament_id: msg.tournament_id,
            native_token: msg.native_token,
            decline_token: msg.decline_token,
            livekit_url: msg.livekit_url,
            livekit_token: msg.livekit_token,
            callerName: msg.from?.name,
            callerAvatar: msg.from?.avatar_url,
          });
          return;
        }
        setState((prev) => {
          if (prev.phase !== "idle") {
            // Busy với CUỘC GỌI KHÁC → auto-reject silently. KHÔNG reject
            // khi cùng call_id (autoAnswerById có thể đã bắt máy TRƯỚC khi
            // event này tới — reject sẽ giết cuộc gọi đang active).
            if (prev.call?.call_id !== msg.call_id) {
              api.post(`/calls/${msg.call_id}/reject`).catch(() => {});
            }
            return prev;
          }
          try {
            const audio = new Audio(
              "data:audio/wav;base64,UklGRl4EAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YToEAAA=",
            );
            audio.play().catch(() => {});
          } catch { /* noop */ }
          const isNativeShell2 = typeof window !== "undefined" && window.__pickleagueNativeApp === true;
          if (!isNativeShell2) {
            requestNativeMicPermission();
            prewarmMic();
            joinRoom(msg, { publishMic: false }).catch(() => {});
          }
          return {
            phase: "incoming",
            call: {
              call_id: msg.call_id,
              room_name: msg.room_name,
              tournament_id: msg.tournament_id,
              livekit_url: msg.livekit_url,
              livekit_token: msg.livekit_token,
              native_token: msg.native_token,
              decline_token: msg.decline_token,
            },
            peer: msg.from || null,
          };
        });
      } else if (t === "call.answered") {
        if (msg.by && msg.by === user.id) {
          // Chính mình đã bắt máy ở tab/thiết bị khác → tab này tắt chuông.
          if (!answeringRef.current) {
            const current = stateRef.current;
            if (current.phase === "incoming" && current.call?.call_id === msg.call_id) {
              resetIdle();
            }
          }
        } else {
          onPeerAnswered();
        }
      } else if (t === "call.ended") {
        const cur = stateRef.current;
        if (cur.phase === "idle") return;
        if (cur.call?.call_id && msg.call_id && cur.call.call_id !== msg.call_id) return;
        if (!(msg.by && msg.by === user.id)) {
          toast.info(msg.reason === "rejected" ? "Người nhận đã từ chối" : msg.reason === "cancelled" ? "Cuộc gọi đã bị huỷ" : "Cuộc gọi đã kết thúc");
        }
        resetIdle();
      }
    };

    // Subscribe to hub. Hub server bắn `call.*` payloads qua channel
    // `notifications` (per-user_id ACL — chỉ user tương ứng nhận).
    const unsub = wsSubscribe("notifications", (msg) => {
      // Hub payload: có thể là notification thường {type:"notification",data:...}
      // hoặc call signaling {type:"call.incoming",...} (không wrap).
      if (msg?.type && String(msg.type).startsWith("call.")) {
        onMsg(msg);
      }
    });

    return () => {
      unsub();
    };
  }, [user?.id, onPeerAnswered, resetIdle, prewarmMic, joinRoom, postNativeIncomingCall]);

  // ─── Polling fallback cho signaling ───
  // WS push có thể bị lạc: PROD chạy nhiều instance (hub in-memory per
  // instance) hoặc mobile kill socket. Poll DB làm nguồn tin cậy thứ 2:
  //   • idle → GET /calls/pending (mỗi ~7.5s) để không lỡ chuông.
  //   • outgoing/incoming/active → GET /calls/{id} (mỗi 2.5s) để đồng bộ
  //     answered/rejected/cancelled/ended → tắt popup cả 2 bên.
  useEffect(() => {
    if (!user?.id) return undefined;
    const OPERATOR = ["referee", "admin", "organizer", "staff", "super_admin"];
    const canReceive = (user.roles || []).some((r) => OPERATOR.includes(r));
    let busy = false;
    let tickCount = 0;
    const tick = async () => {
      if (busy) return;
      busy = true;
      tickCount += 1;
      try {
        const cur = stateRef.current;
        if (cur.phase === "idle") {
          // v17.perf.har2 (02/2026) — Idle poll từ 7.5s (mỗi 3 tick) → 25s
          // (mỗi 10 tick). WS `call.incoming` là kênh chính (đã subscribe qua
          // hub notifications ở effect trên). Poll chỉ là safety net cross-
          // instance / mobile socket kill. HAR PROD 18x/151s → target ~6x.
          if (!canReceive || tickCount % 10 !== 0) return;
          const r = await api.get("/calls/pending").catch(() => null);
          const c = r?.data?.call;
          if (c && stateRef.current.phase === "idle") {
            const isNativeShell = typeof window !== "undefined" && window.__pickleagueNativeApp === true;
            if (isNativeShell) {
              postNativeIncomingCall({
                call_id: c.id,
                room_name: c.room_name,
                tournament_id: c.tournament_id,
                native_token: c.native_token,
                decline_token: c.decline_token,
                livekit_url: c.livekit_url,
                livekit_token: c.livekit_token,
                callerName: c.peer?.name,
                callerAvatar: c.peer?.avatar_url,
              });
              return;
            }
            requestNativeMicPermission();
            prewarmMic();
            joinRoom(c, { publishMic: false }).catch(() => {});
            setState({
              phase: "incoming",
              call: {
                call_id: c.id,
                room_name: c.room_name,
                tournament_id: c.tournament_id,
                livekit_url: c.livekit_url,
                livekit_token: c.livekit_token,
                native_token: c.native_token,
                decline_token: c.decline_token,
              },
              peer: c.peer || null,
            });
          }
        } else if (cur.call?.call_id) {
          const r = await api.get(`/calls/${cur.call.call_id}`).catch(() => null);
          const st = r?.data?.status;
          if (!st) return;
          if (stateRef.current.call?.call_id !== cur.call.call_id) return;
          if (cur.phase === "outgoing" && st === "active") {
            onPeerAnswered();
          } else if (cur.phase === "incoming" && st === "active" && !answeringRef.current) {
            // Đã bắt máy ở tab/thiết bị khác → tắt chuông tab này.
            await resetIdle();
          } else if (["rejected", "cancelled", "ended"].includes(st) && stateRef.current.phase !== "idle") {
            toast.info(st === "rejected" ? "Người nhận đã từ chối" : st === "cancelled" ? "Cuộc gọi đã bị huỷ" : "Cuộc gọi đã kết thúc");
            await resetIdle();
          }
        }
      } catch { /* noop */ } finally {
        busy = false;
      }
    };
    const iv = setInterval(tick, 2500);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        tickCount = 9; // v17.perf.har2 — buộc lần tick tiếp theo check pending ngay (idle poll mỗi 10 tick)
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user?.id, user?.roles, onPeerAnswered, resetIdle, prewarmMic, joinRoom, postNativeIncomingCall]);

  // PROD có nhiều backend instance nên call.answered WS có thể lạc. Trong
  // đúng giai đoạn outgoing/ringing, poll nhanh để caller đã pre-connect chỉ
  // mất tối đa ~350ms trước khi publish mic; không tăng tải lúc idle/active.
  useEffect(() => {
    if (state.phase !== "outgoing" || !state.call?.call_id) return undefined;
    let busy = false;
    const callId = state.call.call_id;
    const tick = async () => {
      if (busy || joiningRef.current) return;
      busy = true;
      try {
        const response = await api.get(`/calls/${callId}`).catch(() => null);
        if (response?.data?.status === "active" && stateRef.current.phase === "outgoing") {
          onPeerAnswered();
        }
      } finally {
        busy = false;
      }
    };
    const timer = setInterval(tick, 350);
    return () => clearInterval(timer);
  }, [state.phase, state.call?.call_id, onPeerAnswered]);

  // Phase 90.F — Người dùng bấm "Trả lời" trên màn hình cuộc gọi native (app
  // mobile). Native inject CustomEvent này → tự bắt máy khi cuộc gọi tới.
  // Phase 90.H — Persist qua localStorage để survive page load (khi app mở từ
  // trạng thái kill, CallProvider có thể chưa mount lúc event dispatch).
  // 25-07 (Bug 1+2 fix) — Auto-answer TRỰC TIẾP theo call_id:
  //   • App đang mở, web ĐÃ đổ chuông sẵn → state không đổi → effect
  //     "incoming khớp pending" không re-run → trước đây phải bấm tay.
  //   • Web CHƯA nhận call.incoming (WS lạc/cold-start) → trước đây phải
  //     chờ polling (tới 7.5s) mới auto-answer → delay lớn.
  //   → Giờ POST /answer thẳng bằng call_id từ native, không chờ chuông.
  const AUTO_ANSWER_STORAGE_KEY = "pl_pending_auto_answer_v1";
  const AUTO_ANSWER_TTL_MS = 60_000;
  const pendingAutoAnswerRef = useRef(null); // { callId, ts }
  const answerCallRef = useRef(null);
  answerCallRef.current = answerCall;

  const clearPendingAutoAnswer = useCallback(() => {
    pendingAutoAnswerRef.current = null;
    try { window.localStorage?.removeItem(AUTO_ANSWER_STORAGE_KEY); } catch { /* noop */ }
  }, []);

  const autoAnswerById = useCallback(async (callId) => {
    if (!callId || answeringRef.current) return;
    const cur = stateRef.current;
    // Đã active/ending với đúng cuộc gọi này → không làm gì thêm.
    if (cur.call?.call_id === callId && cur.phase !== "incoming") return;
    // Đang bận cuộc gọi khác → bỏ qua.
    if (cur.phase !== "idle" && cur.call?.call_id !== callId) return;
    clearPendingAutoAnswer();
    if (cur.phase === "incoming" && cur.call?.call_id === callId) {
      answerCallRef.current?.();
      return;
    }
    // Web chưa nhận call.incoming → trả lời trực tiếp.
    answeringRef.current = true;
    requestNativeMicPermission();
    prewarmMic();
    setState({ phase: "active", call: { call_id: callId }, peer: null });
    try {
      const { data } = await api.post(`/calls/${callId}/answer`);
      setState({ phase: "active", call: data, peer: data.peer || null });
      await joinRoom(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Không thể trả lời cuộc gọi");
      await resetIdle();
    } finally {
      answeringRef.current = false;
    }
  }, [clearPendingAutoAnswer, joinRoom, prewarmMic, resetIdle]);
  const autoAnswerByIdRef = useRef(null);
  autoAnswerByIdRef.current = autoAnswerById;

  // Đọc từ localStorage khi mount (native đã ghi trước khi web load xong).
  useEffect(() => {
    try {
      const raw = window.localStorage?.getItem(AUTO_ANSWER_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.callId && Date.now() - (parsed.ts || 0) < AUTO_ANSWER_TTL_MS) {
          pendingAutoAnswerRef.current = { callId: parsed.callId, ts: parsed.ts };
          autoAnswerByIdRef.current?.(parsed.callId);
        } else {
          window.localStorage.removeItem(AUTO_ANSWER_STORAGE_KEY);
        }
      }
    } catch { /* noop */ }
  }, []);
  useEffect(() => {
    const onNativeAnswer = (e) => {
      const callId = e?.detail?.call_id;
      if (!callId) return;
      const ts = Date.now();
      pendingAutoAnswerRef.current = { callId, ts };
      try {
        window.localStorage?.setItem(AUTO_ANSWER_STORAGE_KEY, JSON.stringify({ callId, ts }));
      } catch { /* noop */ }
      // Bug 1 fix: bắt máy NGAY — hoạt động cả khi web đang đổ chuông sẵn
      // lẫn khi chưa nhận incoming.
      autoAnswerByIdRef.current?.(callId);
    };
    window.addEventListener("PickLeagueAnswerCall", onNativeAnswer);
    return () => window.removeEventListener("PickLeagueAnswerCall", onNativeAnswer);
  }, []);

  // Fallback: khi có cuộc gọi incoming khớp với pending auto-answer → bắt máy.
  useEffect(() => {
    if (state.phase !== "incoming" || !state.call?.call_id) return;
    const pending = pendingAutoAnswerRef.current;
    if (!pending) return;
    if (Date.now() - pending.ts > AUTO_ANSWER_TTL_MS) {
      clearPendingAutoAnswer();
      return;
    }
    if (pending.callId === state.call.call_id) {
      clearPendingAutoAnswer();
      answerCall();
    }
  }, [state.phase, state.call?.call_id, answerCall, clearPendingAutoAnswer]);

  const value = useMemo(
    () => ({
      phase: state.phase,
      call: state.call,
      peer: state.peer,
      muted,
      remoteConnected,
      audioBlocked,
      speakerOn,
      initiateCall,
      answerCall,
      rejectCall,
      endCall,
      toggleMute,
      toggleSpeaker,
      enableAudio,
    }),
    [state, muted, remoteConnected, audioBlocked, speakerOn, initiateCall, answerCall, rejectCall, endCall, toggleMute, toggleSpeaker, enableAudio],
  );

  return <CallCtx.Provider value={value}>{children}</CallCtx.Provider>;
}

export function useCall() {
  const ctx = useContext(CallCtx);
  if (!ctx) {
    // Return a safe no-op stub so consumers rendered outside the provider
    // don't crash (e.g. login page).
    return {
      phase: "idle",
      call: null,
      peer: null,
      muted: false,
      remoteConnected: false,
      audioBlocked: false,
      speakerOn: false,
      initiateCall: async () => {},
      answerCall: async () => {},
      rejectCall: async () => {},
      endCall: async () => {},
      toggleMute: async () => {},
      toggleSpeaker: () => {},
      enableAudio: async () => {},
    };
  }
  return ctx;
}
